import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, signal, effect } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { KnobModule } from 'primeng/knob';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { RecruitingCallCounts, CallGoals } from 'src/app/api/types/recruiting';
import { DashboardDataService } from '../../../../services/dashboard-data.service';
import { IconService } from 'src/app/ui/icon.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

@Component({
  selector: 'app-call-progress',
  standalone: true,
  imports: [CardModule, KnobModule, FormsModule, TooltipModule, DecimalPipe, FontAwesomeModule],
  templateUrl: './call-progress.component.html',
  styleUrl: './call-progress.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CallProgressComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();

  // Signals for call counts and goals
  private callCounts = signal<RecruitingCallCounts | null>(null);
  private callGoals = signal<CallGoals | null>(null);

  currentYear = new Date().getFullYear();
  currentMonth = new Date().toLocaleString('default', { month: 'long' });

  // Computed values for each section
  weekProgress = computed(() => {
    const counts = this.callCounts();
    const goals = this.callGoals();
    if (!counts || !goals) return { percentage: 0, value: 0, goal: 0 };
    const value = counts.week.total;
    const goal = goals.callWeek;
    return {
      percentage: goal > 0 ? Math.round((value / goal) * 100) : 0,
      value,
      goal
    };
  });

  monthProgress = computed(() => {
    const counts = this.callCounts();
    const goals = this.callGoals();
    if (!counts || !goals) return { percentage: 0, value: 0, goal: 0 };
    const value = counts.month.total;
    const goal = goals.callMonth;
    return {
      percentage: goal > 0 ? Math.round((value / goal) * 100) : 0,
      value,
      goal
    };
  });

  yearProgress = computed(() => {
    const counts = this.callCounts();
    const goals = this.callGoals();
    if (!counts || !goals) return { percentage: 0, value: 0, goal: 0 };
    const value = counts.year.total;
    const goal = goals.callYear;
    return {
      percentage: goal > 0 ? Math.round((value / goal) * 100) : 0,
      value,
      goal
    };
  });

  constructor(
    private dashboardDataService: DashboardDataService,
    public icons: IconService
  ) {
    // Use effects to watch signals
    effect(() => {
      const counts = this.dashboardDataService.callCounts();
      this.callCounts.set(counts);
    });

    effect(() => {
      const goals = this.dashboardDataService.callGoals();
      this.callGoals.set(goals);
    });
  }

  ngOnInit() {
    // Initialize data loading
    this.dashboardDataService.initializeData();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
} 